// Предзагрузка картинок игры при входе: фоны замка и полей, здания, иконки ресурсов, карта мира.
// Картинка скачивается (дальше лежит в кэше Service Worker на телефоне 30 дней) и сразу распаковывается
// в память — при переходе между замком, полями и картой фон появляется мгновенно, без «проявления».
// Второстепенное (воины своей расы, герои, корабли, значки карты) догружается потом в фоне.
import { MAP_MARKER_URLS, mapCastleArtUrls } from '@/map/mapArt';
import { SHIP_TYPES } from '@sk/shared';
import { V8_VERSION } from '@/map/v8version';
import {
  SHIP_DIRS,
  artImage,
  buildingArtUrl,
  heroArtUrl,
  resourceArtUrl,
  shipArtUrl,
  shipSpriteUrl,
  unitPortraitArtUrl,
} from './artAssets';
import manifest from './artManifest.json';
import { FIELDS_BG } from './backgrounds';
import { raceCastleBg, raceCastleImages } from './raceArt';
import { TOWER_ART_LEVELS, towerArtUrl } from './towerArt';
import { worldGrid } from './worldInfo';

let bootDone = false;

/** Экран загрузки закончился: можно рисовать замок. Его первая отрисовка надолго занимает страницу. */
export function markBootDone(): void {
  bootDone = true;
}

export function isBootDone(): boolean {
  return bootDone;
}

/** Без чего первый экран выглядит недоделанным: фоны, здания, ресурсы. */
export function criticalImages(race?: string | null): string[] {
  return [
    raceCastleBg(race),
    FIELDS_BG,
    ...Object.keys(manifest.buildings).map((b) => buildingArtUrl(b)!),
    ...raceCastleImages(race).slice(1),
    ...['silver', 'wood', 'stone', 'food', 'iron', 'gems'].map(resourceArtUrl),
  ];
}

/** Остальная графика: воины и герои своей расы, монстры, корабли, замки и значки на карте. */
export function secondaryImages(race: string | null | undefined): string[] {
  const r = race ?? 'humans';
  const races = ['humans', 'elves', 'dwarves', 'orcs'];
  const units = Object.keys(manifest.units).filter(
    (k) =>
      (k.startsWith(`${r}_`) || !races.some((x) => k.startsWith(`${x}_`))) && !(`${k}_1` in manifest.units),
  );
  const heroes = Object.keys(manifest.heroes).filter((k) => k.startsWith(`${r}_`));
  return [
    ...units.map(unitPortraitArtUrl),
    ...heroes.map((k) => heroArtUrl(r, k.slice(r.length + 1))!).filter(Boolean),
    ...SHIP_TYPES.flatMap((t) => [shipArtUrl(t), ...SHIP_DIRS.map((d) => shipSpriteUrl(t, d))]),
    ...mapCastleArtUrls(),
    ...MAP_MARKER_URLS,
    ...TOWER_ART_LEVELS.map(towerArtUrl),
  ];
}

/** Скачать и распаковать картинку; ошибка не останавливает загрузку остальных. */
async function warm(url: string): Promise<void> {
  const img = artImage(url);
  try {
    if (!img.complete || !img.naturalWidth)
      await new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      });
    if (img.naturalWidth) await img.decode().catch(() => undefined);
  } catch {
    // нет картинки — нарисуется запасная векторная
  }
}

/** Загрузка списка с ограничением одновременных запросов; onStep — после каждой картинки. */
export async function preloadImages(urls: string[], onStep?: () => void, parallel = 6): Promise<void> {
  const queue = [...new Set(urls)];
  const worker = async () => {
    for (let url = queue.shift(); url; url = queue.shift()) {
      await warm(url);
      onStep?.();
    }
  };
  await Promise.all(Array.from({ length: Math.min(parallel, queue.length) }, worker));
}

/**
 * Шаги загрузки при входе — по очереди, чтобы на медленной сети главное успело первым:
 * 1) фоны замка и полей, 2) здания и иконки ресурсов, 3) карта мира (код экрана, география, текстуры)
 * и код частых экранов (армия, герои, походы).
 */
export function criticalSteps(race?: string | null): {
  total: number;
  run: (onStep: () => void) => Promise<void>;
} {
  const images = criticalImages(race);
  const backgrounds = images.slice(0, 2);
  const rest = images.slice(2);
  const screens = [
    () => import('@/pages/game/MapPage'),
    () => import('@/pages/game/Army'),
    () => import('@/pages/game/Heroes'),
    () => import('@/pages/game/Marches'),
  ];
  return {
    total: images.length + 2 + screens.length,
    run: async (onStep) => {
      await preloadImages(backgrounds, onStep, 2);
      await preloadImages(rest, onStep);
      await Promise.all([
        // Рельефная карта v8: высоты и обзорная текстура — в кэш браузера заранее
        Promise.all(
          [
            '/art/world/v8/manifest.json',
            '/art/world/v8/height.bin',
            '/art/world/v8/albedo/overview.webp',
          ].map((u) => fetch(`${u}?v=${V8_VERSION}`).then((r) => r.arrayBuffer())),
        )
          .catch(() => undefined)
          .finally(onStep),
        worldGrid()
          .catch(() => undefined)
          .finally(onStep),
        ...screens.map((load) =>
          load()
            .catch(() => undefined)
            .finally(onStep),
        ),
      ]);
    },
  };
}
